import React from 'react';
import { Body, Headline } from '@sato/components';
import * as yup from 'yup';

import { IWidget, ParsedEntry } from '@/contentful/types';

import { api } from '@api';
import { HookFormFileInput } from '@components/ReactHookFormFields/HookFormFileInput';
import { HookFormTextArea } from '@components/ReactHookFormFields/HookFormTextArea';
import { HookFormTextInput } from '@components/ReactHookFormFields/HookFormTextInput';
import { BODY_MAX_LENGTH, TOPIC_MAX_LENGTH } from '@constants';
import { useIncidentCreation } from '@hooks/incidents';
import { useCustomTranslations } from '@hooks/useCustomTranslations';
import { ApartmentAccessMethod, PetsInformation } from '@models/api';
import {
  IncidentClassification,
  IncidentSubClassification,
} from '@models/Incident';

import {
  IncidentFormWrapper,
  IncidentReportFields,
} from '../../IncidentFormWrapper';

import { ApartmentAccessField } from './ApartmentAccessField';

export type ApartmentDefectForm = IncidentReportFields & {
  pets: PetsInformation;
  access: ApartmentAccessMethod;
  info?: string;
};

export const ApartmentDefectCommonForm = ({
  subClassification,
  customTranslations,
}: {
  subClassification: IncidentSubClassification;
} & ParsedEntry<IWidget>) => {
  const { t, tContentful } = useCustomTranslations(customTranslations);

  const { create } = useIncidentCreation({
    postIncident: api.incidents.postApartmentDefectIncident,
    classifications: {
      classification: IncidentClassification.DEFECT_REPORT,
      subClassification,
    },
    messageTitlePrefix: t('messageTitlePrefix'),
  });

  return (
    <IncidentFormWrapper<ApartmentDefectForm>
      incidentKind="ApartmentDefectCommon"
      defaultValues={{
        title: '',
        description: '',
        files: [],
        pets: undefined,
        access: ApartmentAccessMethod.OK,
        info: '',
      }}
      validationSchema={yup.object().shape({
        title: yup.string().required(),
        description: yup.string().required(),
        pets: yup.string().oneOf(Object.values(PetsInformation)).required(),
        access: yup
          .string()
          .oneOf(Object.values(ApartmentAccessMethod))
          .required(),
        info: yup.string(),
        files: yup.array().files(tContentful),
      })}
      onSubmit={create}
    >
      <div className="flex flex-col gap-3">
        <Headline level={3}>
          {tContentful('defectFormDescriptionTitle')}
        </Headline>

        <Body>{tContentful('defectFormDescriptionBody')}</Body>
      </div>

      <HookFormTextInput
        type="text"
        name="title"
        label={{ value: t('titleLabel'), type: 'static' }}
        placeholder={t('titlePlaceholder')}
        maxLength={TOPIC_MAX_LENGTH}
      />

      <HookFormTextArea
        type="text"
        name="description"
        label={{ value: t('descriptionLabel'), type: 'static' }}
        placeholder={t('descriptionPlaceholder')}
        message={t('descriptionHelper')}
        rows={6}
        maxLength={BODY_MAX_LENGTH}
      />

      <HookFormFileInput
        accept="application/pdf,image/jpeg,image/png"
        name="files"
        message={tContentful('supported_attachment_files')}
        label={tContentful('choose_files')}
      />

      <ApartmentAccessField />
    </IncidentFormWrapper>
  );
};
